import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaArrowRight, FaChalkboardTeacher, FaEnvelope, FaPen, FaPlus, FaSearch, FaTrash, FaUserCheck } from "react-icons/fa";
import { AuthContext } from "../../../AuthProvider";
import CopyButton from "../../../components/CopyButton";
import ErrorPage from "../../../components/ErrorPage";
import Modal from "../../../components/Modal";
import Spinner from "../../../components/Spinner";
import { useCan } from "../../../authz/useCan";
import { deleteTeacher, linkTeacherSelf } from "../api";
import { getCachedTeachers, hasTeacherListCache, loadTeacherList, removeCachedTeacher, upsertCachedTeacher } from "../cache";
import { ActionButton, AvatarBlock, EmptyState, IconActionButton, LdsPage, ListSection, SearchPanel, StatusBadge, listRowClass } from "../components/Lds";
import type { AppError, EntityId, Teacher } from "../types";
import { backendEmailAssessment, entityKey, httpStatus, isLikelyValidEmailFormat, teacherName } from "../utils";

const Teachers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { keycloak } = useContext(AuthContext);

  const canListTeachers = useCan("school.teachers", "list");
  const canReadTeachers = useCan("school.teachers", "read");
  const canCrudTeachers = useCan("school.teachers", "crud");
  const canManageTeachers = useCan("school.teachers", "manage");
  const canLinkSelf = useCan("school.teachers", "linkSelf");
  const canViewTeachers = canListTeachers || canReadTeachers || canCrudTeachers || canManageTeachers;
  const canOpenTeachers = canViewTeachers || canLinkSelf;
  const canEditTeachers = canCrudTeachers || canManageTeachers;

  const [teachers, setTeachers] = useState<Teacher[]>(() => getCachedTeachers() ?? []);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(() => !hasTeacherListCache());
  const [error, setError] = useState<AppError | null>(null);
  const [teacherToDelete, setTeacherToDelete] = useState<EntityId | null>(null);
  const [linkBusyId, setLinkBusyId] = useState<EntityId | null>(null);

  const currentSub = keycloak?.tokenParsed?.sub ? String(keycloak.tokenParsed.sub) : null;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!canOpenTeachers) {
        setError({ message: t("error.403.message", { defaultValue: "You do not have permission." }), status: 403 });
        setLoading(false);
        return;
      }
      try {
        const data = await loadTeacherList();
        if (!cancelled) setTeachers(data);
      } catch (err) {
        if (!cancelled && !hasTeacherListCache()) {
          setError({
            message:
              httpStatus(err) === 403
                ? t("error.403.message", { defaultValue: "You do not have permission." })
                : t("teachers.management.load_failed", { defaultValue: "Failed to load teachers." }),
            status: httpStatus(err),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [canOpenTeachers, t]);

  const alreadyLinked = useMemo(
    () => Boolean(currentSub && teachers.some((teacher) => teacher.keycloakUserId === currentSub)),
    [currentSub, teachers]
  );

  const filteredTeachers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return teachers;
    return teachers.filter((teacher) =>
      `${teacher.firstName ?? ""} ${teacher.lastName ?? ""} ${teacher.email ?? ""}`.toLowerCase().includes(query)
    );
  }, [searchQuery, teachers]);

  const handleLinkSelf = async (teacher: Teacher) => {
    if (!canLinkSelf || alreadyLinked) return;
    setLinkBusyId(teacher.id);
    try {
      const updated = await linkTeacherSelf(teacher.id);
      const next = updated ?? { ...teacher, keycloakUserId: currentSub };
      upsertCachedTeacher(next);
      setTeachers((prev) => prev.map((item) => (String(item.id) === String(teacher.id) ? next : item)));
    } catch (err) {
      setError({
        message:
          httpStatus(err) === 409
            ? t("teachers.link.conflict", { defaultValue: "Already linked to another account." })
            : httpStatus(err) === 403
            ? t("error.403.message", { defaultValue: "You do not have permission." })
            : t("teachers.link.failed", { defaultValue: "Failed to link your account." }),
        status: httpStatus(err),
      });
    } finally {
      setLinkBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!teacherToDelete) return;
    try {
      await deleteTeacher(teacherToDelete);
      removeCachedTeacher(teacherToDelete);
      setTeachers((prev) => prev.filter((teacher) => String(teacher.id) !== String(teacherToDelete)));
      setTeacherToDelete(null);
    } catch (err) {
      setError({
        message:
          httpStatus(err) === 403
            ? t("error.403.message", { defaultValue: "You do not have permission." })
            : t("teachers.management.delete_failed", { defaultValue: "Failed to delete teacher." }),
        status: httpStatus(err),
      });
    }
  };

  if (loading && teachers.length === 0) return <Spinner />;
  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;

  return (
    <LdsPage>
      <div className="space-y-5">
        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
                <FaChalkboardTeacher aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("school.overview.title", { defaultValue: "School structure" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  {t("teachers.management.title", { defaultValue: "Teachers" })}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("teachers.management.description", { defaultValue: "Manage teachers, account links and classroom assignments." })}
                </p>
                {loading && teachers.length > 0 ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <StatusBadge tone="info">{t("common.refreshing", { defaultValue: "Refreshing" })}</StatusBadge>
                  </div>
                ) : null}
              </div>
            </div>

            {canEditTeachers ? (
              <div className="ml-auto shrink-0 lg:mr-36">
                <ActionButton onClick={() => navigate("/school/teachers/new")}>
                  <span className="inline-flex items-center gap-2">
                    <FaPlus aria-hidden="true" />
                    {t("teachers.management.add_button", { defaultValue: "Add teacher" })}
                  </span>
                </ActionButton>
              </div>
            ) : null}
          </div>

          <div className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex" aria-hidden="true">
            <FaChalkboardTeacher className="text-[110px] text-violet-200/70 dark:text-violet-800/40" />
          </div>
        </section>

        <SearchPanel>
          <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2 dark:border-white/10 dark:bg-slate-950/50">
            <FaSearch className="text-slate-400" />
            <input
              type="text"
              placeholder={t("teachers.common.search_placeholder", { defaultValue: "Search teachers..." })}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="min-w-0 flex-1 bg-transparent text-sm text-slate-950 outline-none placeholder:text-slate-400 dark:text-white"
            />
          </label>
        </SearchPanel>

        <ListSection
          title={t("teachers.management.list_title", { defaultValue: "Teacher overview" })}
          count={t("common.result_count", { count: filteredTeachers.length, defaultValue: "{{count}} results" })}
        >
          {filteredTeachers.map((teacher) => {
            const name = teacherName(teacher) || t("teachers.table.unknown_teacher", { defaultValue: "Unknown teacher" });
            const isMyRow = Boolean(currentSub && teacher.keycloakUserId === currentSub);
            const isLinked = Boolean(teacher.keycloakUserId);
            const backend = backendEmailAssessment(teacher);
            const showInvalid = backend?.status === "invalid" || (!backend && Boolean(teacher.email) && !isLikelyValidEmailFormat(teacher.email));
            const showSuspicious = backend?.status === "suspicious";
            return (
              <article
                key={entityKey(teacher.id)}
                onClick={() => canViewTeachers && navigate(`/school/teachers/${teacher.id}/details`)}
                className={`${listRowClass} ${canViewTeachers ? "" : "cursor-default"} lg:grid-cols-[minmax(240px,1.05fr)_minmax(220px,0.9fr)_160px_150px_auto]`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <AvatarBlock>{name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("") || "?"}</AvatarBlock>
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-semibold text-slate-950 dark:text-white">{name}</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t("teachers.management.row_subtitle", { defaultValue: "Teacher record" })}</p>
                  </div>
                </div>

                <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                  <FaEnvelope aria-hidden="true" className="shrink-0 text-indigo-500 dark:text-indigo-300" />
                  <span className="truncate" title={teacher.email || ""}>{teacher.email || t("common.not_available", { defaultValue: "Not available" })}</span>
                  <CopyButton
                    value={teacher.email}
                    label={t("common.copy_email", { defaultValue: "Copy email address" })}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <StatusBadge tone={isLinked ? "success" : "warn"}>
                    <span className="inline-flex items-center gap-1">
                      <FaUserCheck aria-hidden="true" />
                      {isLinked ? t("teachers.link.status_linked", { defaultValue: "Linked" }) : t("teachers.edit.not_linked_badge", { defaultValue: "Not linked" })}
                    </span>
                  </StatusBadge>
                  {showInvalid ? <StatusBadge tone="danger">{t("teachers.email.invalid_format", { defaultValue: "Invalid format" })}</StatusBadge> : null}
                  {showSuspicious ? <StatusBadge tone="warn">{t("teachers.email.suspicious", { defaultValue: "Suspicious email" })}</StatusBadge> : null}
                </div>

                <div className="flex flex-wrap gap-2">
                  {isMyRow ? <StatusBadge tone="info">{t("teachers.link.status_linked_you", { defaultValue: "Linked to you" })}</StatusBadge> : null}
                  {canLinkSelf && !isLinked && !alreadyLinked ? (
                    <ActionButton tone="success" disabled={String(linkBusyId) === String(teacher.id)} onClick={() => handleLinkSelf(teacher)}>
                      {String(linkBusyId) === String(teacher.id) ? t("common.working", { defaultValue: "Working..." }) : t("teachers.link.this_is_me", { defaultValue: "This is me" })}
                    </ActionButton>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center justify-start gap-2 lg:justify-end">
                  {canViewTeachers ? (
                    <ActionButton tone="secondary" onClick={() => navigate(`/school/teachers/${teacher.id}/details`)}>
                      <span className="inline-flex items-center gap-2">{t("common.actions.view", { defaultValue: "View" })}<FaArrowRight aria-hidden="true" /></span>
                    </ActionButton>
                  ) : null}
                  {canEditTeachers ? (
                    <>
                      <IconActionButton label={t("common.actions.edit", { defaultValue: "Edit" })} onClick={() => navigate(`/school/teachers/${teacher.id}/edit`)}>
                        <FaPen aria-hidden="true" />
                      </IconActionButton>
                      <IconActionButton label={t("common.actions.delete", { defaultValue: "Delete" })} tone="danger" onClick={() => setTeacherToDelete(teacher.id)}>
                        <FaTrash aria-hidden="true" />
                      </IconActionButton>
                    </>
                  ) : null}
                </div>
              </article>
            );
          })}
          {filteredTeachers.length === 0 ? <div className="p-5"><EmptyState>{t("teachers.management.none_available", { defaultValue: "No teachers available." })}</EmptyState></div> : null}
        </ListSection>
      </div>

      <Modal
        show={teacherToDelete != null}
        title={t("common.modal.are_you_sure", { defaultValue: "Are you sure?" })}
        message={t("common.modal.action_cannot_be_undone", { defaultValue: "This action cannot be undone." })}
        isConfirm
        type="confirm"
        onConfirm={handleDelete}
        onClose={() => setTeacherToDelete(null)}
      />
    </LdsPage>
  );
};

export default Teachers;
